import { useMemo, useState } from 'react';
import { SIZES_ML } from '../../shared/catalog';
import { ISO_COUNTRIES, QUICK_COUNTRIES, countryCode, countryLongName } from '../../shared/countries';
import { sizeLabel } from '../format';

type Props =
  | {
      optional?: false;
      country: string;
      sizeMl: number;
      onCountry: (code: string) => void;
      onSize: (ml: number) => void;
    }
  | {
      /** Adds "Geen voorkeur" to both rows; it gives null. */
      optional: true;
      nullLabel?: string;
      country: string | null;
      sizeMl: number | null;
      onCountry: (code: string | null) => void;
      onSize: (ml: number | null) => void;
    };

const quickCodes = new Set(QUICK_COUNTRIES.map(([c]) => c));

export function VariantPicker(props: Props) {
  const { country, sizeMl, onCountry, onSize } = props;
  const [searching, setSearching] = useState(false);
  const [query, setQuery] = useState('');
  const [customSize, setCustomSize] = useState<string | null>(null);

  const countries = useMemo(() => {
    const list = QUICK_COUNTRIES.map(([c]) => c);
    if (country && !quickCodes.has(country)) list.push(country);
    return list;
  }, [country]);

  const results = useMemo(() => {
    const q = query.trim().toLowerCase();
    return ISO_COUNTRIES.map((code) => ({ code, name: countryLongName(code) }))
      .filter(({ code, name }) => !q || name.toLowerCase().includes(q) || code.toLowerCase() === q || countryCode(code).toLowerCase() === q)
      .sort((a, b) => a.name.localeCompare(b.name, 'nl'))
      .slice(0, 60);
  }, [query]);

  const sizes: number[] =
    sizeMl === null || SIZES_ML.includes(sizeMl as (typeof SIZES_ML)[number])
      ? [...SIZES_ML]
      : [...SIZES_ML, sizeMl].sort((a, b) => a - b);

  const commitCustom = () => {
    const ml = Number(customSize);
    if (Number.isInteger(ml) && ml >= 50 && ml <= 2000) onSize(ml);
    setCustomSize(null);
  };

  return (
    <>
      <div className="label section-label">Land van herkomst</div>
      <div className="countries">
        {props.optional && (
          <button
            type="button"
            className="chip country-chip"
            aria-pressed={country === null}
            onClick={() => props.onCountry(null)}
          >
            {props.nullLabel ?? 'Geen voorkeur'}
          </button>
        )}
        {countries.map((code) => (
          <button
            key={code}
            type="button"
            className="chip country-chip"
            aria-label={countryLongName(code)}
            aria-pressed={country === code}
            onClick={() => onCountry(code)}
          >
            {countryCode(code)}
          </button>
        ))}
        <button
          type="button"
          className="chip country-chip"
          aria-expanded={searching}
          onClick={() => setSearching((s) => !s)}
        >
          {searching ? 'Sluit' : 'Ander land…'}
        </button>
      </div>
      {searching && (
        <div className="country-search">
          <input
            className="input"
            type="search"
            placeholder="Zoek een land"
            value={query}
            autoFocus
            onChange={(e) => setQuery(e.target.value)}
            aria-label="Zoek een land"
          />
          <ul>
            {results.map(({ code, name }) => (
              <li key={code}>
                <button
                  type="button"
                  onClick={() => {
                    onCountry(code);
                    setSearching(false);
                    setQuery('');
                  }}
                >
                  <small>{countryCode(code)}</small>
                  {name}
                </button>
              </li>
            ))}
          </ul>
        </div>
      )}

      <div className="label section-label" style={{ marginTop: 20 }}>
        Inhoud
      </div>
      <div className="sizes">
        {props.optional && (
          <button
            type="button"
            className="chip size-chip size-chip--any"
            aria-pressed={sizeMl === null}
            onClick={() => props.onSize(null)}
          >
            {props.nullLabel ?? 'Geen voorkeur'}
          </button>
        )}
        {sizes.map((ml) => (
          <button key={ml} type="button" className="chip size-chip" aria-pressed={sizeMl === ml} onClick={() => onSize(ml)}>
            {sizeLabel(ml)}
          </button>
        ))}
        {customSize === null ? (
          <button type="button" className="chip size-chip" onClick={() => setCustomSize('')}>
            Anders…
          </button>
        ) : (
          <input
            className="input input--mono"
            style={{ height: 42, gridColumn: 'span 2' }}
            inputMode="numeric"
            pattern="[0-9]*"
            placeholder="ml, bijv. 443"
            aria-label="Inhoud in ml"
            autoFocus
            value={customSize}
            onChange={(e) => setCustomSize(e.target.value.replace(/\D/g, ''))}
            onBlur={commitCustom}
            onKeyDown={(e) => e.key === 'Enter' && commitCustom()}
          />
        )}
      </div>
    </>
  );
}
